import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';

// import styles from '../Home/CreateExam/Create.module.scss';
import styles from './AssignExamModal.module.scss';
import { showToast } from "../../Components/ToastMessage";

import {
    createExamOnline,
    getAlluser,
    getDepartment,
    getPosition,
} from '../../redux/apiRequest';

const cx = classNames.bind(styles);

const clampNumber = (value) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

function AssignExamModal({ onClose, user, axiosJWT, selectedSubjects, onExamCreated  }) {
    const dispatch = useDispatch();

    // form state
    const [title, setTitle] = useState('');
    const [duration, setDuration] = useState({ hours: '', minutes: '', seconds: '' });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [positionFilter, setPositionFilter] = useState('');
    const [formErrors, setFormErrors] = useState({
        title: '',
        duration: '',
        users: '',
    });

    // redux data
    const userState = useSelector((state) => state.auth.allusers);
    const departmentsState = useSelector((state) => state.departments);
    const positionsState = useSelector((state) => state.positions);

    const users = Array.isArray(userState?.listusers?.users) ? userState.listusers.users : [];
    const departmentsList = Array.isArray(departmentsState?.departments?.listdepartments)
        ? departmentsState.departments.listdepartments
        : [];
    const positionsList = Array.isArray(positionsState?.positions?.listpositions)
        ? positionsState.positions.listpositions
        : [];
    useEffect(() => {
        if (user?.accessToken) {
            getAlluser(user.accessToken, dispatch, axiosJWT);
            getDepartment(user.accessToken, dispatch, axiosJWT);
            getPosition(user.accessToken, dispatch, axiosJWT);
        }
    }, [user?.accessToken, dispatch, axiosJWT]);

    const totalDurationSeconds = useMemo(() => {
        const hours = clampNumber(duration.hours);
        const minutes = clampNumber(duration.minutes);
        const seconds = clampNumber(duration.seconds);
        return hours * 3600 + minutes * 60 + seconds;
    }, [duration]);

    // filter user list
    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            const depOk = departmentFilter ? String(u.department) === String(departmentFilter) : true;
            const posOk = positionFilter ? String(u.position) === String(positionFilter) : true;
            return depOk && posOk;
        });
    }, [users, departmentFilter, positionFilter]);


    const toggleUser = (id) => {
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
        );
    };

    const validateForm = () => {
        const errors = { title: '', duration: '', users: '' };
        let hasErrors = false;

        if (!title.trim()) {
            errors.title = 'Vui lòng nhập tên đề thi.';
            hasErrors = true;
        }

        if (totalDurationSeconds <= 0) {
            errors.duration = 'Thời gian làm bài phải lớn hơn 0.';
            hasErrors = true;
        }

        if (selectedUsers.length === 0) {
            errors.users = 'Vui lòng chọn ít nhất một thí sinh.';
            hasErrors = true;
        }

        setFormErrors(errors);
        return !hasErrors;
    };

    const handleAssign = async () => {
        if (!validateForm()) return;

        const payload = {
            title: title.trim(),
            duration_seconds: totalDurationSeconds,
            selectedSubjects: selectedSubjects.map((s) => ({
                subjectId: s.subjectId,
                numQuestions: Number(s.numQuestions) || 0,
            })),
            assignedUsers: selectedUsers.map((id) => ({ userId: id })), // ✅ sửa chỗ này
        };

        const successCallback = async () => {
            showToast({
                type: "success",
                title: "Thành công!",
                message: "Tạo và phát bài thi thành công.",
                duration: 3000,
            });
            if (typeof onClose === 'function') onClose(false);
                if (typeof onExamCreated === 'function') onExamCreated();  // đóng CreateExam + clear state
        };

        const errCallback = async () => {
            showToast({
                            type: "error",
                            title: "Thất bại!",
                            message: "Tạo và phát bài thi thất bại. Vui lòng thử lại.",
                            duration: 3000,
                        });
            if (typeof onClose === 'function') onClose(false);
        };
        await createExamOnline(dispatch, user?.accessToken, payload, axiosJWT, successCallback, errCallback);
    };

    const handleClose = () => {
        if (typeof onClose === 'function') onClose(false);
    };

    const handleDurationInput = (field) => (event) => {
        setDuration((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    return (
        <div className={cx('overlay')}>
            <div className={cx('modal')}>
                <button type="button" className={cx('close-button')} onClick={handleClose} aria-label="Đóng">
                    ×
                </button>

                <header className={cx('header')}>
                    <span className={cx('header__subtitle')}>Phát đề thi</span>
                    <h2 className={cx('header__title')}>Nhập thông tin và chọn thí sinh</h2>
                </header>

                {/* exam info */}
                <section className={cx('subject-section')}>
                    <h3>Thông tin đề thi</h3>
                    <div className={cx('subject-row')} style={{ gridTemplateColumns: '1fr', gap: 16 }}>
                        <div className={cx('field')}>
                            <label htmlFor="exam-title">Tên</label>
                            <input
                                id="exam-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Nhập tên đề thi"
                            />
                            {formErrors.title && <div className={cx('error-message')}>{formErrors.title}</div>}
                        </div>
                    </div>

                    <div
                        className={cx('subject-row')}
                        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}
                    >
                        <div className={cx('field')}>
                            <label>Giờ</label>
                            <input
                                type="number"
                                min="0"
                                value={duration.hours}
                                onChange={handleDurationInput('hours')}
                                placeholder="0"
                            />
                        </div>
                        <div className={cx('field')}>
                            <label>Phút</label>
                            <input
                                type="number"
                                min="0"
                                value={duration.minutes}
                                onChange={handleDurationInput('minutes')}
                                placeholder="0"
                            />
                        </div>
                        <div className={cx('field')}>
                            <label>Giây</label>
                            <input
                                type="number"
                                min="0"
                                value={duration.seconds}
                                onChange={handleDurationInput('seconds')}
                                placeholder="0"
                            />
                        </div>
                    </div>
                    {formErrors.duration && <div className={cx('error-message')}>{formErrors.duration}</div>}
                </section>

                <section className={cx('filter-row')}>
                    <h3 className={cx('filter-title')}>Chọn thí sinh</h3>

                    <div className={cx('filter-field')}>
                        <label>Chức vụ</label>
                        <select
                            value={positionFilter}
                            onChange={(e) => setPositionFilter(e.target.value)}
                        >
                            <option value="">Tất cả</option>
                            {positionsList.map((p) => (
                                <option key={p._id} value={p._id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={cx('filter-field')}>
                        <label>Phòng ban</label>
                        <select
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                        >
                            <option value="">Tất cả</option>
                            {departmentsList.map((d) => (
                                <option key={d._id} value={d._id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </section>
                <div className={cx('user-list')}>
                    {filteredUsers.length === 0 && <p>Không có thí sinh nào.</p>}
                    {filteredUsers.map((u) => (
                        <label key={u._id} className={cx('user-item')}>
                            <input
                                type="checkbox"
                                checked={selectedUsers.includes(u._id)}
                                onChange={() => toggleUser(u._id)}
                            />
                            {u.fullname}
                        </label>
                    ))}
                </div>
                {formErrors.users && <div className={cx('error-message')}>{formErrors.users}</div>}
                <footer className={cx('footer')}>
                    <div className={cx('actions')}>
                        <button type="button" className={cx('secondary-button')} onClick={handleClose}>
                            Huỷ
                        </button>
                        <button type="button" className={cx('primary-button')} onClick={handleAssign}>
                            Phát bài thi
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default AssignExamModal;
